import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, message } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import {
  getInvoices,
  getSubscriptionOverview,
  deleteInvoice,
  getInvoiceDetails,
} from "../../../services/AdminPortalAPIs";
import StatCard from "src/components/reusable/StatCard";
import LoadingSpinner from "src/components/reusable/LoadingSpinner"; // Make sure this exists
import StyledTable from "../../../components/reusable/StyledTable";
import InvoiceDetailsModal from "src/components/reusable/InvoiceDetailsModal";
import { exportInvoiceAsPDF } from "src/layouts/AdminPortal/Subscriptions/InvoiceExporter";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledInput from "src/components/reusable/StyledInput";

type InvoiceItem = {
  id: string;
  customer: string;
  plan: string;
  amount: string;
  status: string;
  date: string;
  dueDate: string;
  next: string;
};

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [overview, setOverview] = useState<{
    total_revenue: { amount: number; percentage_change: string };
    pending_payments: { amount: number; count: number };
    overdue_payments: { amount: number; count: number };
  } | null>(null);
  const didFetchRef = React.useRef(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;
    const fetchInvoicesAndOverview = async () => {
      try {
        setLoading(true);
        const [invoiceRes, overviewRes] = await Promise.all([
          getInvoices(),
          getSubscriptionOverview(),
        ]);

        if (invoiceRes.data.status) {
         const mapped: InvoiceItem[] = invoiceRes.data.data
  .map((inv: any): InvoiceItem => ({
    id: inv.payment_id,
    customer: inv.tenant_name,
    plan: inv.plan,
    amount: `$${inv.total_amount.toFixed(2)}`,
    status: inv.status.charAt(0).toUpperCase() + inv.status.slice(1),
    date: inv.date,
    dueDate: inv.due_date,
    next: inv.next_billing,
  }))
  .sort((a: InvoiceItem, b: InvoiceItem) => new Date(b.date).getTime() - new Date(a.date).getTime());

setInvoices(mapped);
        } else {
          console.error("Failed to fetch invoices:", invoiceRes.data.message);
        }

        if (overviewRes.data.status) {
          setOverview(overviewRes.data.data);
        } else {
          console.error("Failed to fetch overview:", overviewRes.data.message);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoicesAndOverview();
  }, []);

  const handleDelete = async () => {
    if (!selectedInvoiceId) {return;}

    setDeleteLoading(true); // Start spinner

    try {
      const res = await deleteInvoice(selectedInvoiceId);
      if (res.data.status) {
        message.success("Invoice deleted successfully");
        setInvoices(prev =>
          prev.filter(inv => inv.id !== selectedInvoiceId)
        );
      } else {
        message.error(res.data.message || "Failed to delete invoice");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      message.error("An error occurred while deleting the invoice.");
    } finally {
      setDeleteLoading(false); // Stop spinner
      setShowDeleteModal(false);
      setSelectedInvoiceId(null);
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const matchesSearch =
        inv.id.toLowerCase().includes(searchText.toLowerCase()) ||
        inv.customer.toLowerCase().includes(searchText.toLowerCase());

      const matchesStatus =
        selectedStatus === "All" || inv.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchText, selectedStatus]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2;

  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredInvoices.slice(startIndex, startIndex + pageSize);
  }, [filteredInvoices, currentPage]);

  const invoiceColumns = [
    {
      title: "Invoice ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: string) => {
        const statusClass =
          `status-badge--${text.toLowerCase()}` || "status-badge--neutral";

        return <span className={`status-badge ${statusClass}`}>{text}</span>;
      },
    },
    {
      title: "Generated On",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
    },
    {
      title: "Next Billing",
      dataIndex: "next",
      key: "next",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, inv: any) => (
        <>
          <div
            className="flex gap-2"
            style={{ position: "relative", overflow: "visible" }}
          >
            <Tippy content="View Invoice" placement="top">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedInvoice(inv);
                  setShowViewModal(true);
                }}
              />
            </Tippy>

            <Tippy content="Edit Invoice" placement="top">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin-portal/invoice/edit/${inv.id}`)}
              />
            </Tippy>

            <Tippy content="Delete Invoice" placement="top">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setSelectedInvoiceId(inv.id);
                  setShowDeleteModal(true);
                }}
              />
            </Tippy>

         <Tippy content="Download as PDF" placement="top">
  <Button
    type="text"
    icon={<DownloadOutlined />}
    onClick={async () => {
      try {
        const res = await getInvoiceDetails(inv.id); // fetch details from backend
        if (res.data.status) {
          exportInvoiceAsPDF({
            tenant_name: res.data.data.tenant_name,
            billing_address: res.data.data.billing_address, // ✅ real address
            invoice_number: res.data.data.invoice_number,
            next_billing_date: res.data.data.next_billing_date,
            payment_date: res.data.data.payment_date,
            due_date: res.data.data.due_date,
            status: res.data.data.status.toLowerCase(),
            package_name: res.data.data.package_name,
            package_price: res.data.data.package_price,
            total_price: res.data.data.total_price,
            additional_user_seats: 0,
            price_per_extra_user: 0,
            extra_users_cost: 0,
            notes: res.data.data.notes,
          });
        } else {
          console.error("Failed to fetch invoice details:", res.data.message);
        }
      } catch (error) {
        console.error("Error fetching invoice details:", error);
      }
    }}
  />
</Tippy>
          </div>
        </>
      ),
    },
  ];

  const statusColorMap: Record<string, string> = {
    Paid: "bg-green-100 text-green-800",
    Pending: "bg-yellow-100 text-yellow-800",
    Overdue: "bg-red-100 text-red-800",
  };

  return (
    <div className="admin-portal-page">
      <span className="heading-primary">Subscriptions & Billing</span>

      <Row gutter={16} className="field-labels">
        <Col span={8}>
          <StatCard
            label="Total Revenue"
            value={
              overview ? `$${overview.total_revenue.amount.toFixed(2)}` : "--"
            }
            description={
              overview
                ? overview.total_revenue.percentage_change + " vs last month"
                : ""
            }
          />
        </Col>
        <Col span={8}>
          <StatCard
            label="Pending Payments"
            value={
              overview
                ? `$${overview.pending_payments.amount.toFixed(2)}`
                : "--"
            }
            description={
              overview ? `${overview.pending_payments.count} invoice(s)` : ""
            }
          />
        </Col>
        <Col span={8}>
          <StatCard
            label="Overdue"
            value={
              overview
                ? `$${overview.overdue_payments.amount.toFixed(2)}`
                : "--"
            }
            description={
              overview ? `${overview.overdue_payments.count} invoice(s)` : ""
            }
          />
        </Col>
      </Row>

      <div className="invoice-management">
        <span className="heading-secondary">Invoice Management</span>

        <div className="filters">
       <StyledInput
  placeholder="Search by Invoice ID or Customer Name"
  onChange={e => setSearchText(e.target.value)}
  className="admin-search"
/>
<StyledDropdown
  value={selectedStatus}
  onChange={setSelectedStatus}
  popupClassName="custom-dropdown-menu"
  options={[
    { value: "All", label: "All Statuses" },
    { value: "Paid", label: "Paid" },
    { value: "Pending", label: "Pending" },
    { value: "Overdue", label: "Overdue" },
  ]}
  style={{ width: 180 }}
  placeholder="Select Status"
/>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : filteredInvoices.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <p>No invoices found matching your criteria.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 10,
              }}
            >
              <Button
                className="button button--primary"
                type="primary"
                aria-label="Create New Invoice"
                onClick={() => navigate("/admin-portal/invoice/create")}
              >
                + Create Invoice
              </Button>
            </div>

            <StyledTable
              columns={invoiceColumns}
              dataSource={filteredInvoices}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                onChange: page => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: undefined,
              }}
              locale={{ emptyText: "No invoices found." }}
              className="pipeline-table" // Key: apply scoped styles
            />

            {filteredInvoices.length > pageSize && (
              <div className="custom-pagination">
                {/* ...render buttons here */}
              </div>
            )}

            <InvoiceDetailsModal
              visible={showViewModal}
              onClose={() => {
                setShowViewModal(false);
                setSelectedInvoice(null);
              }}
              invoice={selectedInvoice}
            />

            <ReusableModal
              modalOpen={showDeleteModal}
              onCancel={() => setShowDeleteModal(false)}
              onConfirm={handleDelete}
              title="Delete Invoice"
              description="Are you sure you want to delete this invoice? This action cannot be undone."
              icon="/Icons/invoice-del.svg"
              confirmText="Yes, Delete"
              cancelText="Cancel"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
